/**
 * Block validation utilities for Amazon Flex scheduling rules
 */

// Valid block durations in hours
export const VALID_DURATIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

// Buffer time after a block ends before another can start (in minutes)
export const BLOCK_BUFFER_MINUTES = 30;

// Hour limits
export const HOUR_LIMITS = {
  regular: { weekly: 40, daily: 8 },
  extended: { weekly: 50, daily: 10 },
};

/**
 * Parse time string "HH:MM" to minutes from midnight
 */
export function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Format minutes to "HH:MM" string
 */
export function minutesToTime(minutes: number): string {
  const normalizedMinutes = ((minutes % 1440) + 1440) % 1440; // Handle negative/overflow
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

/**
 * Calculate block duration in hours
 */
export function calculateBlockDuration(shiftStart: string, shiftEnd: string): number {
  const startMinutes = parseTimeToMinutes(shiftStart);
  let endMinutes = parseTimeToMinutes(shiftEnd);

  // Handle overnight shifts
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  return (endMinutes - startMinutes) / 60;
}

/**
 * Check if time is on a valid 15-minute interval (00, 15, 30, 45)
 */
export function isValidTimeInterval(time: string): boolean {
  if (!time || !time.includes(":")) return false;
  const minutes = parseTimeToMinutes(time) % 60;
  return [0, 15, 30, 45].includes(minutes);
}

/**
 * Get validation error for time interval
 */
export function getTimeIntervalError(time: string, fieldName: string): string | null {
  if (!time) return null;
  if (!isValidTimeInterval(time)) {
    return `${fieldName} must be on 15-minute intervals (00, 15, 30, or 45)`;
  }
  return null;
}

/**
 * Check if block duration is valid (1hr to 5hr in 30min increments)
 */
export function isValidBlockDuration(shiftStart: string, shiftEnd: string): boolean {
  if (!shiftStart || !shiftEnd) return true; // Can't validate incomplete data
  const duration = calculateBlockDuration(shiftStart, shiftEnd);
  return VALID_DURATIONS.includes(duration);
}

/**
 * Get validation error for block duration
 */
export function getDurationError(shiftStart: string, shiftEnd: string): string | null {
  if (!shiftStart || !shiftEnd) return null;

  const duration = calculateBlockDuration(shiftStart, shiftEnd);

  if (duration <= 0) {
    return "End time must be after start time";
  }

  if (!VALID_DURATIONS.includes(duration)) {
    return `Block must be ${VALID_DURATIONS.join(", ")} hours. Current: ${duration}hr`;
  }

  return null;
}

export interface ExistingBlock {
  _id: string;
  date: string;
  shiftStart: string;
  shiftEnd: string;
}

/**
 * Check if a new block conflicts with existing blocks
 * Returns the conflicting block if found, null otherwise
 */
export function findConflictingBlock(
  date: string,
  shiftStart: string,
  shiftEnd: string,
  existingBlocks: ExistingBlock[],
  excludeBlockId?: string
): ExistingBlock | null {
  if (!date || !shiftStart || !shiftEnd) return null;

  const newStartMinutes = parseTimeToMinutes(shiftStart);
  let newEndMinutes = parseTimeToMinutes(shiftEnd);
  if (newEndMinutes <= newStartMinutes) newEndMinutes += 24 * 60;

  // Filter blocks for the same date, excluding the block being edited
  const sameDayBlocks = existingBlocks.filter(
    (block) => block.date === date && block._id !== excludeBlockId
  );

  for (const block of sameDayBlocks) {
    const existingStartMinutes = parseTimeToMinutes(block.shiftStart);
    let existingEndMinutes = parseTimeToMinutes(block.shiftEnd);
    if (existingEndMinutes <= existingStartMinutes) existingEndMinutes += 24 * 60;

    // Add 30-minute buffer after existing block ends
    const bufferEndMinutes = existingEndMinutes + BLOCK_BUFFER_MINUTES;

    // Check for overlap: new block starts before buffer ends AND new block ends after existing starts
    // New block cannot start until 30 min after previous block ends
    if (newStartMinutes < bufferEndMinutes && newEndMinutes > existingStartMinutes) {
      return block;
    }
  }

  return null;
}

/**
 * Get the earliest available start time after existing blocks
 */
export function getEarliestAvailableTime(
  date: string,
  existingBlocks: ExistingBlock[]
): string | null {
  const sameDayBlocks = existingBlocks.filter((block) => block.date === date);

  if (sameDayBlocks.length === 0) return null;

  // Find the latest ending block
  let latestEnd = 0;
  for (const block of sameDayBlocks) {
    const endMinutes = parseTimeToMinutes(block.shiftEnd);
    if (endMinutes > latestEnd) {
      latestEnd = endMinutes;
    }
  }

  // Add buffer and round to next 15-minute interval
  const earliestStart = latestEnd + BLOCK_BUFFER_MINUTES;
  const roundedStart = Math.ceil(earliestStart / 15) * 15;

  return minutesToTime(roundedStart);
}

/**
 * Get conflict error message
 */
export function getConflictError(
  date: string,
  shiftStart: string,
  shiftEnd: string,
  existingBlocks: ExistingBlock[],
  excludeBlockId?: string
): string | null {
  const conflict = findConflictingBlock(date, shiftStart, shiftEnd, existingBlocks, excludeBlockId);

  if (!conflict) return null;

  const earliestTime = getEarliestAvailableTime(date, existingBlocks);

  return `Conflicts with block ${conflict.shiftStart}-${conflict.shiftEnd}. ` +
    `Must wait 30 min after block ends. ` +
    (earliestTime ? `Earliest start: ${earliestTime}` : "");
}

export interface HoursCheckResult {
  currentWeeklyHours: number;
  blockHours: number;
  totalAfterBlock: number;
  weeklyLimit: number;
  exceedsLimit: boolean;
  wouldFitWithExtended: boolean;
}

/**
 * Check if adding a block would exceed hour limits
 */
export function checkHoursLimit(
  shiftStart: string,
  shiftEnd: string,
  currentWeeklyHours: number,
  extendedHours: boolean
): HoursCheckResult {
  const blockHours = calculateBlockDuration(shiftStart, shiftEnd);
  const totalAfterBlock = currentWeeklyHours + blockHours;
  const weeklyLimit = extendedHours ? HOUR_LIMITS.extended.weekly : HOUR_LIMITS.regular.weekly;
  const exceedsLimit = totalAfterBlock > weeklyLimit;
  const wouldFitWithExtended = totalAfterBlock <= HOUR_LIMITS.extended.weekly;

  return {
    currentWeeklyHours,
    blockHours,
    totalAfterBlock,
    weeklyLimit,
    exceedsLimit,
    wouldFitWithExtended,
  };
}

/**
 * Validate all block fields and return errors
 */
export function validateBlock(
  date: string | undefined,
  shiftStart: string,
  shiftEnd: string,
  existingBlocks: ExistingBlock[],
  excludeBlockId?: string
): { field: string; message: string }[] {
  const errors: { field: string; message: string }[] = [];

  // Time interval validation
  const startIntervalError = getTimeIntervalError(shiftStart, "Start time");
  if (startIntervalError) {
    errors.push({ field: "shiftStart", message: startIntervalError });
  }

  const endIntervalError = getTimeIntervalError(shiftEnd, "End time");
  if (endIntervalError) {
    errors.push({ field: "shiftEnd", message: endIntervalError });
  }

  // Check that end time is after start time (basic sanity check)
  const duration = calculateBlockDuration(shiftStart, shiftEnd);
  if (shiftStart && shiftEnd && duration <= 0) {
    errors.push({ field: "duration", message: "End time must be after start time" });
  }

  // Conflict validation
  if (date) {
    const conflictError = getConflictError(
      date,
      shiftStart,
      shiftEnd,
      existingBlocks,
      excludeBlockId
    );
    if (conflictError) {
      errors.push({ field: "conflict", message: conflictError });
    }
  }

  return errors;
}
